import React, { useRef } from "react";
import "./ui.scss";
import "./FormControls.scss";

/**
 * a11y attributes for a form control, wired to its hint / error message.
 *   <input {...fieldA11y("reason", { error: errors.reason })} />
 */
export const fieldA11y = (id, { error, hint } = {}) => ({
  id,
  "aria-invalid": error ? "true" : undefined,
  "aria-describedby": error ? `${id}-error` : hint ? `${id}-hint` : undefined,
});

/**
 * Label + control + hint / error. Pass the control as children and spread
 * fieldA11y() onto it. For a group of controls (e.g. SegmentedControl) pass
 * labelAs="span" and a labelId instead of htmlFor semantics.
 */
export const FormField = ({
  id,
  label,
  required = false,
  hint,
  error,
  labelAs = "label",
  labelId,
  className = "",
  children,
}) => {
  const Label = labelAs;
  return (
    <div className={`hr-form-field ${error ? "has-error" : ""} ${className}`}>
      {label && (
        <Label
          className="hr-form-label"
          id={labelId}
          {...(labelAs === "label" ? { htmlFor: id } : {})}
        >
          {label}
          {required && (
            <span className="hr-required" aria-hidden="true">
              *
            </span>
          )}
        </Label>
      )}
      {children}
      {hint && !error && (
        <p className="hr-form-hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {error && (
        <p className="hr-form-error" id={`${id}-error`}>
          {error}
        </p>
      )}
    </div>
  );
};

/**
 * Compact radio-style switch (e.g. Full Day / First Half / Second Half).
 * Arrow keys move the selection, like a native radio group.
 */
export const SegmentedControl = ({
  id,
  labelledBy,
  options,
  value,
  onChange,
}) => {
  const refs = useRef([]);

  const handleKeyDown = (e, index) => {
    const forward = e.key === "ArrowRight" || e.key === "ArrowDown";
    const backward = e.key === "ArrowLeft" || e.key === "ArrowUp";
    if (!forward && !backward) return;
    e.preventDefault();
    const next = (index + (forward ? 1 : -1) + options.length) % options.length;
    onChange(options[next].value);
    if (refs.current[next]) refs.current[next].focus();
  };

  return (
    <div
      id={id}
      className="hr-segmented"
      role="radiogroup"
      aria-labelledby={labelledBy}
    >
      {options.map((option, index) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            className={`hr-segmented__btn ${active ? "is-active" : ""}`}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
